import React, { useCallback } from "react";

import { FancyLink, MultiplyIcon } from "shared/components";
import { Icon, MutedDarkText, StatusText, TinyText } from "shared/styles";
import { formatNumber } from "shared/utils";
import { IStage, IStageCapacity } from "Venues/types";
import { StageCapacities, StageCapacity, StageInfoContainer } from "./styles";

import { map, max, min } from "lodash-es";
import { copy } from "shared/assets";
import { Venue } from "shared/assets/icons";
import { Spacer } from "shared/layout";
import { IStatus } from "shared/types";
import { VENUE_PATHS } from "Venues/router";
import {
  capacityTypeOptions,
  getStageUrlWithEvent,
  useVenueRoute,
} from "Venues/utils";
const { venuePage } = copy;

interface StageInfoProps {
  venueId: string;
  stages?: IStage[];
  editBlockUrl?: string | null;
  eventId?: string;
}

export const StageInfo: React.FC<StageInfoProps> = ({
  venueId,
  stages,
  editBlockUrl,
  eventId,
}) => {
  const { getPath } = useVenueRoute();
  const getStageUrl = useCallback(
    (stageId: string) =>
      eventId
        ? getStageUrlWithEvent(venueId, stageId, eventId)
        : getPath(VENUE_PATHS.STAGE, { venueId, stageId }),
    [eventId, getPath, venueId]
  );

  const renderCapacitiesList = (capacities: IStageCapacity[]) =>
    capacities?.map(({ id, capacity, type }, index) => (
      <React.Fragment key={id}>
        <StageCapacity>
          <span>{formatNumber(capacity)}</span>
          <MultiplyIcon />
          <span>{capacityTypeOptions[type]}</span>
        </StageCapacity>
        <MutedDarkText>{index !== capacities.length - 1 && "|"}</MutedDarkText>
      </React.Fragment>
    ));

  const renderCapacities = (capacities: IStageCapacity[]) => {
    if (capacities.length > 4) {
      const capacitiesSize = map(capacities, "capacity");
      const minCapacity = min(capacitiesSize);
      const maxCapacity = max(capacitiesSize);

      return (
        <StageCapacity>
          <span>{venuePage.view.capacities.multiple}</span>
          <MutedDarkText>
            <TinyText>
              ({minCapacity && formatNumber(minCapacity)} -{" "}
              {maxCapacity && formatNumber(maxCapacity)})
            </TinyText>
          </MutedDarkText>
        </StageCapacity>
      );
    }

    return renderCapacitiesList(capacities);
  };

  const renderEditLink = useCallback(
    (link: string) => (
      <FancyLink to={link} status={IStatus.warning}>
        {venuePage.view.editLinkedEvent.button}
      </FancyLink>
    ),
    []
  );

  if (!stages?.length && editBlockUrl)
    return (
      <StageInfoContainer size="xs" isActive>
        <Spacer
          size="xs"
          spaceDirection="horizontal"
          align="center"
          flexWrap="nowrap"
        >
          <StatusText status={IStatus.warning}>
            {venuePage.view.editLinkedEvent.noCapacitySelected}
          </StatusText>
          {renderEditLink(editBlockUrl)}
        </Spacer>
      </StageInfoContainer>
    );

  if (!stages?.length) return null;

  return (
    <StageInfoContainer
      size="xs"
      data-testid="venue_header_stages"
      isActive={!!editBlockUrl}
    >
      {stages?.map(({ id: stageId, title, stageCapacities }) => (
        <Spacer
          size="xs"
          spaceDirection="horizontal"
          align="center"
          flexWrap="nowrap"
          key={stageId}
          data-testid={stageId}
        >
          <Icon as={Venue} />
          <StageCapacities>
            <FancyLink to={getStageUrl(stageId)}>{title}</FancyLink>
            {stageCapacities && renderCapacities(stageCapacities)}
          </StageCapacities>
          {editBlockUrl && renderEditLink(editBlockUrl)}
        </Spacer>
      ))}
    </StageInfoContainer>
  );
};
